






import { defineComponent, onBeforeUnmount, ref } from "@vue/composition-api";
import { AgeVerificationStatus } from "@scrile/api-provider/dist/api/AgeVerificationProvider";
import useAuthUser from "@/hooks/useAuthUser";
import emitter from "@/lib/emitter";
import { useAgeVerification } from "@/features/age-verification";
import { EVENTS } from "@/features/age-verification/const";
import AgeVerificationStatusView from "../AgeVerification/components/AgeVerificationStatusView";

const STATUS_TIMER_MS = 5000;

export default defineComponent({
  name: "AgeVerificationStatusBanner",
  components: { AgeVerificationStatusView },
  setup() {
    const { authUser } = useAuthUser();
    const { status, get } = useAgeVerification();

    const showStatus = ref(false);
    let generation = 0;
    let listener: (() => Promise<void>) | null = null;

    const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));
    const isCancelled = (token: number) => token !== generation;
    const cancel = () => {
      generation += 1;
      showStatus.value = false;
    };

    const stopListening = () => {
      cancel();
      if (listener) {
        emitter.off(EVENTS.AGE_VERIFICATION_STATUS_CHANGED, listener);
        listener = null;
      }
    };

    const pollWhilePending = async (token: number) => {
      while (!isCancelled(token) && status.value === AgeVerificationStatus.PENDING) {
        showStatus.value = true;
        await wait(STATUS_TIMER_MS);
        if (isCancelled(token)) return;
        await get();
      }
    };

    const showResultAndHide = async (token: number) => {
      const s = status.value;
      if (s === AgeVerificationStatus.APPROVED || s === AgeVerificationStatus.DENIED) {
        showStatus.value = true;
        await wait(STATUS_TIMER_MS);
        if (!isCancelled(token)) showStatus.value = false;
      } else {
        showStatus.value = false;
      }
      if (s === AgeVerificationStatus.APPROVED) stopListening();
    };

    const run = async (): Promise<void> => {
      const token = ++generation;

      await get();
      if (isCancelled(token)) return;

      if (status.value === AgeVerificationStatus.APPROVED) {
        stopListening();
        return;
      }
      if (status.value !== AgeVerificationStatus.PENDING) return;

      await pollWhilePending(token);
      if (isCancelled(token)) return;

      await showResultAndHide(token);
    };

    const onRetry = () => {
      cancel();
      emitter.emit(EVENTS.SENSITIVE_CONTENT_GATE_REQUEST);
    };

    if (authUser.value) {
      listener = run;
      emitter.on(EVENTS.AGE_VERIFICATION_STATUS_CHANGED, run);
      run();
    }

    onBeforeUnmount(stopListening);

    return {
      status,
      showStatus,
      onRetry,
    };
  },
});
